import { Injectable, PLATFORM_ID, TransferState, inject, makeStateKey } from '@angular/core';
import { isPlatformServer } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';

import {
  EMPTY,
  Observable,
  of,
  catchError,
  defaultIfEmpty,
  distinctUntilChanged,
  filter,
  map,
  switchMap,
  take,
  tap,
} from 'rxjs';

import { BOOKING_STEPS } from '@fcom/common/config/booking-config';
import { LanguageService } from '@fcom/ui-translate';
import { getBookingStepLink } from '@fcom/common/utils/booking-common.utils';
import {
  ConfigService,
  MonitoringLogger,
  GlobalBookingTravelClass,
  LocationRouteCffService,
  BUS_CONNECTION_LOCATION_CODES,
  isPresent,
} from '@fcom/core';
import { add, TimeUnit, toISO8601 } from '@fcom/core/utils/date';
import { isEmptyObjectOrHasEmptyValues, mapValues, unique, valuesOf } from '@fcom/core/utils/utils';
import { CmsDestination } from '@fcom/core-api';
import { DatePriceList } from '@fcom/dapi/api/models/date-price-list';
import { PriceList } from '@fcom/dapi/api/models/price-list';
import { finShare } from '@fcom/rx';
import { DapiService } from '@fcom/dapi/api/dapi.service';
import { TripType } from '@fcom/core/interfaces/triptype.interface';
import { PriceCalendarWithPricesParams } from '@fcom/common/interfaces/price-calendar.interface';
import { PriceCalendarService } from '@fcom/common/services/price-calendar/price-calendar.service';
import { initialState as globalBookingInitialState } from '@fcom/common/store/reducers/global-booking.reducer';

import { getPricesForOffers } from '../../dapi/api/fn/instantsearch/get-prices-for-offers';
import { postPricesForFlights } from '../../dapi/api/fn/instantsearch/post-prices-for-flights';
import { OndMarketingDataService } from './ond-marketing-data.service';
import {
  OndLocationData,
  OndLocationsResponse,
  OndMarketingOffer,
  OndPairEnhancedResponse,
  OndPairRequestBody,
} from '../interfaces/ond-marketing-offers.interface';
import { constructOndHeadingPath, getCurrentCountry, isCountryCode } from './utils';

export const OFFERS_CACHE_TIME_IN_MILLIS = 60 * 1000; // 1 minute

export interface CityToCityPricesParams {
  origin: string;
  destination: string;
  tripType: TripType;
}

@Injectable()
export class OndMarketingOffersService {
  private monitoringLogger = inject(MonitoringLogger);
  private ondMarketingDataService = inject(OndMarketingDataService);
  private languageService = inject(LanguageService);
  private dapiService = inject(DapiService);
  private configService = inject(ConfigService);
  private locationRouteCffService = inject(LocationRouteCffService);
  private priceCalendarService = inject(PriceCalendarService);
  private transferState = inject(TransferState);
  private isServer = isPlatformServer(inject(PLATFORM_ID));

  private flightsDataCache$: Record<string, { expires: number; stream$: Observable<OndMarketingOffer[]> }> = {};
  private offersDataCache$: Record<string, { expires: number; stream$: Observable<OndMarketingOffer[]> }> = {};
  private locationsDataCache$: Record<string, { expires: number; stream$: Observable<OndLocationsResponse> }> = {};

  pricesAvailable$({ origin, destination, tripType }: CityToCityPricesParams): Observable<boolean | null> {
    const key = `c2c-prices-${origin}-${destination}-${tripType}`;
    const params: PriceCalendarWithPricesParams = {
      origin,
      destination,
      tripType,
      travelClass: globalBookingInitialState.travelClasses.selected,
      paxAmount: globalBookingInitialState.paxAmount,
    };

    if (!this.isServer) {
      return of(this.readTransferredPricesAvailable(key, params));
    }

    return this.priceCalendarService.getPricesForFullYear(params).pipe(
      take(1),
      map(({ fullYear, failed }) => {
        if (failed) {
          return null;
        }
        return Object.keys(fullYear ?? {}).length > 0;
      }),
      defaultIfEmpty(null),
      tap((available) => {
        if (available !== null) {
          this.transferState.set(makeStateKey<boolean>(key), available);
        }
      })
    );
  }

  fetchLocationPairs(requestBody: OndPairRequestBody): Observable<OndPairEnhancedResponse> {
    return this.ondMarketingDataService.fetchLocationPairs(requestBody).pipe(
      filter((pairs) => !isEmptyObjectOrHasEmptyValues(pairs)),
      switchMap((pairs) => {
        const translationsToFetch = valuesOf(pairs)
          .reduce((locationCodes, pair) => locationCodes.concat(pair.origin, pair.destination), [])
          .filter(unique);

        return this.getLocationData(translationsToFetch).pipe(
          map((locations) => {
            return mapValues(pairs, (pair) => {
              return {
                ...pair,
                originCityName: locations[pair.origin]?.cityName,
                originCountryName: locations[pair.origin]?.country,
                originCountryCode: locations[pair.origin]?.countryCode,
                destinationCityName: locations[pair.destination]?.cityName,
                destinationCountryName: locations[pair.destination]?.country,
                destinationCountryCode: locations[pair.destination]?.countryCode,
              };
            });
          })
        );
      }),
      catchError((e: unknown) => this.handleError<OndPairEnhancedResponse>(e, 'Error getting ond location pairs')),
      finShare()
    );
  }

  getDestinationLocationCode(content$: Observable<CmsDestination>): Observable<string> {
    return content$.pipe(
      filter<CmsDestination>(Boolean),
      map((content) => content.locationCode),
      filter<string>(Boolean),
      finShare()
    );
  }

  getHeading(
    originLocationCode: string | undefined,
    destinationLocationCode: string | undefined,
    offers: OndMarketingOffer[],
    headingType: string
  ): Observable<string> {
    const origin = isCountryCode(originLocationCode ?? '') ? offers[0]?.originCountryName : offers[0]?.originCityName;
    const destination = isCountryCode(destinationLocationCode ?? '')
      ? offers[0]?.destinationCountryName
      : offers[0]?.destinationCityName;

    return this.languageService
      .translate(
        constructOndHeadingPath(headingType, {
          destination: destinationLocationCode ?? null,
          origin: originLocationCode ?? null,
        }),
        {
          origin,
          destination,
          originLocationCode: originLocationCode ?? '',
          destinationLocationCode: destinationLocationCode ?? '',
        }
      )
      .pipe(distinctUntilChanged(), finShare());
  }

  getOffersOrFlightData(
    originLocationCode: string | undefined,
    destinationLocationCode: string | undefined,
    links: OndPairEnhancedResponse | undefined
  ): Observable<OndMarketingOffer[]> {
    const origin = originLocationCode ?? '';
    const destinationLocationCodes: string[] = destinationLocationCode
      ? [destinationLocationCode]
      : valuesOf(links ?? {}).map((link) => link.destination);

    const offers$ =
      origin && !isCountryCode(origin)
        ? this.getOfferData(
            destinationLocationCodes.filter((dest) => !BUS_CONNECTION_LOCATION_CODES.includes(dest)),
            origin
          )
        : this.getFlightsData(
            [isCountryCode(origin) ? origin : getCurrentCountry(this.languageService.langValue)],
            destinationLocationCodes,
            isCountryCode(origin) ? 'from' : 'to'
          );

    return offers$.pipe(
      map((offers) =>
        offers
          .filter((off) => isPresent(off.destinationCityName))
          .reduce((acc, offer) => {
            const existingOfferIndex = acc.findIndex(
              (i) => i.destinationLocationCode === offer.destinationLocationCode
            );

            if (existingOfferIndex === -1) {
              acc.push(offer);
            } else if (+offer.price.amount < +acc[existingOfferIndex].price.amount) {
              acc[existingOfferIndex] = offer;
            }

            return acc;
          }, [])
      ),
      finShare()
    );
  }

  private getFlightsData(
    departureLocationCodes: string[],
    destinationLocationCodes: string[],
    direction?: string
  ): Observable<OndMarketingOffer[]> {
    const cacheKey = departureLocationCodes.join('-') + destinationLocationCodes.join('-') + direction;
    if (!this.flightsDataCache$[cacheKey] || Date.now() >= this.flightsDataCache$[cacheKey].expires) {
      const stream$: Observable<OndMarketingOffer[]> = this.dapiService
        .invoke$(postPricesForFlights, this.configService.cfg.instantSearchUrl, {
          body: {
            departureLocationCodes,
            destinationLocationCodes,
            locale: this.languageService.langValue,
          },
        })
        .pipe(
          map((data) =>
            valuesOf(data.prices)
              .map((destinations) => valuesOf(destinations))
              .flat()
              .map(this.flightPriceMapper)
          ),
          switchMap((offers) => this.enhanceOffersWithTranslations(offers)),
          catchError(() => {
            this.evictCacheEntry(this.flightsDataCache$, cacheKey, entry);
            return EMPTY;
          }),
          finShare()
        );
      const entry = {
        stream$,
        expires: Date.now() + OFFERS_CACHE_TIME_IN_MILLIS,
      };
      this.flightsDataCache$[cacheKey] = entry;
    }

    return this.flightsDataCache$[cacheKey].stream$;
  }

  private getOfferData(
    destinationLocationCodes: string[],
    departureLocationCode: string
  ): Observable<OndMarketingOffer[]> {
    const cacheKey = departureLocationCode + destinationLocationCodes.join('-');
    if (!this.offersDataCache$[cacheKey] || Date.now() >= this.offersDataCache$[cacheKey].expires) {
      const stream$: Observable<OndMarketingOffer[]> = this.dapiService
        .invoke$(getPricesForOffers, this.configService.cfg.instantSearchUrl, {
          destinationLocationCodes,
          departureLocationCode,
          departureDate: toISO8601(add(new Date(), 1, TimeUnit.day)),
          numberOfDays: 60,
          lengthOfStay: '3-10',
          travelClasses: [GlobalBookingTravelClass.ECONOMY.toLowerCase()],
        })
        .pipe(
          map((data) =>
            valuesOf(data.prices)
              .map((destinations) => valuesOf(destinations))
              .flat(2)
              .map(this.offersPriceMapper)
          ),
          switchMap((offers) => this.enhanceOffersWithTranslations(offers)),
          catchError((e: unknown) => {
            this.evictCacheEntry(this.offersDataCache$, cacheKey, entry);
            return this.handleError<OndMarketingOffer[]>(e, 'Error getting ond marketing offers data');
          }),
          finShare()
        );
      const entry = {
        stream$,
        expires: Date.now() + OFFERS_CACHE_TIME_IN_MILLIS,
      };
      this.offersDataCache$[cacheKey] = entry;
    }

    return this.offersDataCache$[cacheKey].stream$;
  }

  private enhanceOffersWithTranslations(offers: OndMarketingOffer[]): Observable<OndMarketingOffer[]> {
    const translationsToFetch = offers
      .reduce(
        (locationCodes, offer) => locationCodes.concat(offer.originLocationCode, offer.destinationLocationCode),
        []
      )
      .filter(unique);
    return this.getLocationData(translationsToFetch).pipe(
      map((locations) => {
        return offers.map((offer) => {
          return {
            ...offer,
            originCityName: (locations[offer.originLocationCode] as OndLocationData)?.cityName,
            originCountryName: (locations[offer.originLocationCode] as OndLocationData)?.country,
            originCountryCode: (locations[offer.originLocationCode] as OndLocationData)?.countryCode,
            destinationCityName: (locations[offer.destinationLocationCode] as OndLocationData)?.cityName,
            destinationCountryName: (locations[offer.destinationLocationCode] as OndLocationData)?.country,
            destinationCountryCode: (locations[offer.destinationLocationCode] as OndLocationData)?.countryCode,
          };
        });
      })
    );
  }

  getAirportName(cityName: string, locale: string): Observable<string> {
    if (!cityName || !locale || cityName.length < 3) {
      return of(undefined);
    }
    return this.locationRouteCffService.bestGuessFor(cityName, locale).pipe(
      map((data) => data?.title),
      defaultIfEmpty(undefined),
      finShare()
    );
  }

  getLocationData(destinationCodeArray: string[]): Observable<OndLocationsResponse> {
    const cacheKey = destinationCodeArray.join('-');
    if (!this.locationsDataCache$[cacheKey] || Date.now() >= this.locationsDataCache$[cacheKey].expires) {
      const stream$ =
        destinationCodeArray.length === 0
          ? of({}).pipe()
          : this.ondMarketingDataService.fetchLocations(destinationCodeArray).pipe(
              catchError((e: unknown) => {
                this.evictCacheEntry(this.locationsDataCache$, cacheKey, entry);
                return this.handleError<OndLocationsResponse>(e, 'Error getting ond location data');
              }),
              finShare()
            );
      const entry = {
        stream$,
        expires: Date.now() + OFFERS_CACHE_TIME_IN_MILLIS,
      };
      this.locationsDataCache$[cacheKey] = entry;
    }
    return this.locationsDataCache$[cacheKey].stream$;
  }

  private flightPriceMapper = (priceData: PriceList): OndMarketingOffer => {
    const originLocationCode = priceData.from;
    const destinationLocationCode = priceData.to;
    const pricesForEconomy = priceData.travelClassPrices.find((travelClass) => travelClass.travelClass === 'Economy');
    return {
      originLocationCode,
      destinationLocationCode,
      departureDate: pricesForEconomy.fromDate,
      returnDate: pricesForEconomy.toDate,
      price: {
        amount: pricesForEconomy.price.toString(),
        currencyCode: priceData.currency,
      },
      link: getBookingStepLink(this.languageService.langValue, BOOKING_STEPS.ENTRY, {
        origin: originLocationCode,
        destination: destinationLocationCode,
        departureDate: pricesForEconomy.fromDate,
        returnDate: pricesForEconomy.toDate,
      }),
    };
  };

  private offersPriceMapper = (priceData: DatePriceList): OndMarketingOffer => {
    const originLocationCode = priceData.from;
    const destinationLocationCode = priceData.to;
    const pricesForEconomy = priceData.travelClassPrices.find((travelClass) => travelClass.travelClass === 'Economy');
    return {
      originLocationCode,
      destinationLocationCode,
      departureDate: priceData.fromDate,
      returnDate: priceData.toDate,
      price: {
        amount: pricesForEconomy.price.toString(),
        currencyCode: priceData.currency,
      },
      link: getBookingStepLink(this.languageService.langValue, BOOKING_STEPS.ENTRY, {
        origin: originLocationCode,
        destination: destinationLocationCode,
        departureDate: priceData.fromDate,
        returnDate: priceData.toDate,
      }),
    };
  };

  private readTransferredPricesAvailable(key: string, params: PriceCalendarWithPricesParams): boolean | null {
    const stateKey = makeStateKey<boolean>(key);
    if (!this.transferState.hasKey(stateKey)) {
      return null;
    }

    const value = this.transferState.get<boolean>(stateKey, null);
    this.transferState.remove(stateKey);
    if (value === false) {
      this.priceCalendarService.markKnownNoPrices(params);
    }
    return value;
  }

  private evictCacheEntry<T>(
    cache: Record<string, { expires: number; stream$: Observable<T> }>,
    key: string,
    entry: { expires: number; stream$: Observable<T> }
  ): void {
    if (cache[key] === entry) {
      delete cache[key];
    }
  }

  private handleError<T>(error: unknown, message: string): Observable<T> {
    if (error instanceof HttpErrorResponse && error.error?.error !== 'No location pairs found.') {
      this.monitoringLogger.error(message, { error });
    }
    return EMPTY;
  }
}
